import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { fetchAdminComments, approveComment, deleteComment } from "@/data/blogApi";
import { useToast } from "@/hooks/use-toast";
import { API, withCreds } from "./adminUtils";

const plural = (n, word) => `${n} ${word}${n === 1 ? "y" : "ies"}`;

// Owns the admin session plus all enquiry / comment data and server actions.
export function useAdminData() {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(null); // null = checking, true, false
  const [enquiries, setEnquiries] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [sending, setSending] = useState({ reminder: false, digest: false });

  const handleError = useCallback((err, title) => {
    if (err?.response?.status === 401) {
      setAuthed(false);
      return;
    }
    console.error(title, err);
    toast({ title, description: "Please try again.", variant: "destructive" });
  }, [toast]);

  const loadEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/enquiries`, withCreds);
      setEnquiries(res.data);
    } catch (err) {
      handleError(err, "Couldn't load enquiries");
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const loadComments = useCallback(async () => {
    try {
      setComments(await fetchAdminComments());
    } catch (err) {
      handleError(err, "Couldn't load blog comments");
    }
  }, [handleError]);

  useEffect(() => {
    axios.get(`${API}/admin/me`, withCreds)
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false)); // 401 simply means no active session
  }, []);

  useEffect(() => {
    if (authed === true) {
      loadEnquiries();
      loadComments();
    }
  }, [authed, loadEnquiries, loadComments]);

  const patchEnquiry = async (e, path, body, title) => {
    try {
      const res = await axios.patch(`${API}/admin/enquiries/${e.id}/${path}`, body, withCreds);
      setEnquiries((prev) => prev.map((x) => (x.id === e.id ? res.data : x)));
    } catch (err) {
      handleError(err, title);
    }
  };

  const toggleReplied = async (e) => {
    setBusyId(e.id);
    await patchEnquiry(e, "replied", { replied: !e.replied }, "Couldn't update reply status");
    setBusyId(null);
  };

  const saveNote = (e, note) => patchEnquiry(e, "note", { note }, "Couldn't save note");

  const sendEmail = async (kind, path, format) => {
    setSending((s) => ({ ...s, [kind]: true }));
    try {
      const { data } = await axios.post(`${API}/admin/${path}`, {}, withCreds);
      toast(format(data));
    } catch (err) {
      handleError(err, `Couldn't send ${kind}`);
    } finally {
      setSending((s) => ({ ...s, [kind]: false }));
    }
  };

  const sendReminderNow = () =>
    sendEmail("reminder", "reminders/send", (data) => {
      if (data.sent) return { title: "Reminder sent", description: `${plural(data.count, "overdue enquir")} emailed to you.` };
      const description = data.reason === "nothing overdue" ? "Nothing is overdue — nice work!" : data.reason;
      return { title: "No reminder sent", description };
    });

  const sendDigestNow = () =>
    sendEmail("digest", "digest/send", (data) =>
      data.sent
        ? { title: "Weekly digest sent", description: `Last week's summary (${plural(data.count, "enquir")}) emailed to ${data.to}.` }
        : { title: "Digest not sent", description: data.reason, variant: "destructive" }
    );

  const onApprove = async (id) => {
    try {
      await approveComment(id);
      setComments((prev) => prev.map((c) => (c.id === id ? { ...c, approved: true } : c)));
    } catch (err) {
      handleError(err, "Couldn't approve comment");
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteComment(id);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      handleError(err, "Couldn't delete comment");
    }
  };

  const login = async (key) => {
    if (!key) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/admin/login`, { key }, withCreds);
      setAuthed(true);
    } catch {
      setError("Invalid admin key. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, withCreds);
    } catch (err) {
      console.error("Logout request failed; clearing local session anyway", err);
    }
    setAuthed(false);
    setEnquiries([]);
    setComments([]);
  };

  const refresh = () => { loadEnquiries(); loadComments(); };

  return {
    authed, enquiries, comments, loading, error, busyId, sending,
    login, logout, refresh, toggleReplied, saveNote, sendReminderNow, sendDigestNow, onApprove, onDelete,
  };
}
